// EditorHost pure helpers — the module-level, closure-free edit utilities the
// `useEditorHost` hook composes. Pure: no React, no I/O, no component state.
//
// Split out of `EditorHost/EditorHost.tsx` (C1 hotspot decomposition). These
// were already module-level in that file; lifting them here shrinks the hook
// file to (almost) the hook alone.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { mkRejected } from "../../app/externalError.js";
import { setChoiceFeedback, setElementText } from "../Editor/edit.js";
import type { EditorShape } from "../Editor/Palette/Palette.js";
import type { Element, ElementId, ReviewOutcome } from "../Editor/types.js";
import type { FieldSave, Paths } from "./saveQueue.js";
import type { StructuralRun } from "./useSaveQueue.js";

// A structural save's port call, given the paths of `ids` as they are when
// its turn in the queue comes (an element inserted just before has its path
// by then). One with no path (its insert was refused) refuses the save.
export const atPaths =
    (
        ids: readonly ElementId[],
        send: (
            materialId: string,
            hash: string,
            pathOf: (id: ElementId) => readonly string[],
        ) => ReturnType<StructuralRun["persist"]>,
    ): StructuralRun["persist"] =>
    (materialId: string, hash: string, paths: Paths) =>
        ids.every((id) => paths.has(id))
            ? send(materialId, hash, (id) => paths.get(id) ?? [])
            : Promise.resolve(Result.err(mkRejected("no path for the edit")));

// The insert allow-list for the current (single) template — the SAME shape the
// toolbar's Palette AND the slash menu read, so the two never disagree on what
// can be inserted. `mediaKinds` is empty: media is not an Element kind yet (the
// deferred wiring slice). Closure-free, so it lives here rather than in the hook.
// Where a new empty line goes: the page's start or its end.
export type LinePlace = "first" | "last";

// A blank line shown but not saved yet (the first edit in it saves it).
export type UnsavedLine = {
    readonly materialId: string;
    readonly block: Element;
    readonly at: LinePlace;
};

export const editorShape: EditorShape = {
    rootKinds: ["heading", "prose", "objective", "note", "mcq"],
    inlineKinds: ["strong", "emphasis", "link", "code"],
    mediaKinds: [],
};

// The elements made inside a new element (a question's answers), whose
// paths follow from its own (absent: none).
export const childIdsOf = (
    elements: readonly Element[],
    id: ElementId | undefined,
): readonly ElementId[] => {
    const made = elements.find((e) => e.id === id);
    return made?.type === "mcq" ? made.choices.map((c) => c.id) : [];
};

// Two element paths are the same domain locator when their id segments match in
// order — used to match an in-editor review at an element against the pending
// proposal that targets it.
export const samePath = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((seg, i) => seg === b[i]);

// The primary editable text of a top-level block — the field the block-flow
// "Enter at end" gesture just committed, and the text an Undo compares and
// sends back. Total over Element (a new kind breaks it until named).
export const primaryTextOf = (e: Element): string => {
    switch (e.type) {
        case "heading":
            return e.text;
        case "prose":
            return e.markdown;
        case "objective":
        case "note":
            return e.text;
        case "mcq":
            return e.stem;
        case "part":
            return "";
        default:
            return exhaustSwitchCase(e);
    }
};

// The ReviewOutcome families: accepting an edit/add/remove/move takes the
// agent's change, rejecting keeps the current state. Backing a real proposal,
// the accept family routes to acceptProposal, the reject family to
// rejectProposal.
export const ACCEPT_OUTCOMES: ReadonlySet<ReviewOutcome["type"]> = new Set([
    "add",
    "remove",
    "use-new",
    "keep-both",
    "apply-move",
]);

// One choice whose correct flag moved between two element trees — what a
// correctness edit must persist. Named (never a raw pair) so the persist loop
// reads in domain vocabulary.
type ChoiceCorrectness = {
    readonly choiceId: ElementId;
    readonly correct: boolean;
};

// The choices whose `correct` flag differs between the pre- and post-edit trees.
// Pure: builds an id→correct index over the prior MCQs, then keeps the post
// choices that disagree. On a single-answer mark this yields TWO entries — the
// new key (true) and the cleared prior key (false) — each of which the host
// persists with its own keyed-merge patch. An unchanged tree yields none.
export const affectedCorrectness = (
    prev: readonly Element[],
    next: readonly Element[],
): readonly ChoiceCorrectness[] => {
    const before = new Map<ElementId, boolean>(
        prev.flatMap((e) =>
            e.type === "mcq"
                ? e.choices.map((c) => [c.id, c.correct] as const)
                : [],
        ),
    );
    return next.flatMap((e) =>
        e.type === "mcq"
            ? e.choices
                  .filter((c) => before.get(c.id) !== c.correct)
                  .map((c) => ({ choiceId: c.id, correct: c.correct }))
            : [],
    );
};

// The value one field shows now: a block's primary text or a choice's text,
// or a choice's feedback (undefined: no such field). An edit that equals it
// has nothing to save.
export const fieldValueOf = (
    elements: readonly Element[],
    field: FieldSave,
): string | undefined => {
    const top = elements.find((e) => e.id === field.id);
    const choice = elements
        .flatMap((e) => (e.type === "mcq" ? e.choices : []))
        .find((c) => c.id === field.id);
    switch (field.type) {
        case "text":
            return top !== undefined ? primaryTextOf(top) : choice?.text;
        case "feedback":
            return choice === undefined ? undefined : (choice.feedback ?? "");
        default:
            return exhaustSwitchCase(field);
    }
};

// The tree with one field's new value shown (the optimistic half of a save).
export const withField = (
    elements: readonly Element[],
    field: FieldSave,
): readonly Element[] => {
    switch (field.type) {
        case "text":
            return setElementText(elements, field.id, field.text);
        case "feedback":
            return setChoiceFeedback(elements, field.id, field.feedback);
        default:
            return exhaustSwitchCase(field);
    }
};
